import { graphql } from "gatsby"
import React, { useContext, useMemo } from "react"

import Section from "@atoms/Section/Section"
import Layout from "@components/layout.js"
import SEO from "@components/seo"
import { SiteContext } from "@context/Provider"
import { PricesProvider } from "@context/prices"
import "@lang/i18n"
import i18nWrapper from "@lang/i18nWrapper"
import SearchResultControls from "@organisms/SearchResultControls/SearchResultControls"
import SearchResultWidget from "@organisms/SearchResultWidget/SearchResultWidget"
import SearchTool from "@organisms/SearchTool/SearchTool"

import { SearchProvider } from "@context/search"
import { SearchResultProvider } from "@context/searchResult"

const SearchResult = (props) => {
  const {
    data: {
      area,
      type,
      units,
      wp: {
        themeSettings: {
          themeSettings: {
            groupGlobalPageLinks: { mainSiteSearch },
          },
        },
      },
    },
    pageContext: { seo, language, overrideH2, overrideDescription },
  } = props

  const uri = type?.slug
    ? `/${mainSiteSearch?.slug}/${type.slug}/${area.slug}/`
    : `/${mainSiteSearch?.slug}/${area.slug}/`

  const title = type?.name ? `${type.name}, ${area.name}` : `${area.name}`

  const currentLanguage = language.locale

  const context = useContext(SiteContext)
  context.currentLanguage = currentLanguage
  context.title = title
  context.searchResultH2 = overrideH2
  context.searchResultDescription = overrideDescription

  // Create units grouped by site

  const results = useMemo(() => {
    let sites = {}

    units?.nodes.forEach((unit) => {
      if (unit?.wpParent?.node) {
        sites[unit.wpParent.node.id] = unit.wpParent.node
      }
    })

    return Object.values(sites).map((site) => {
      const siteUnits = units?.nodes
        .filter((unit) => unit?.wpParent?.node.id === site.id)
        .filter(Boolean)

      return {
        siteUnits,
        ...site,
      }
    })
  }, [units])

  return (
    <SearchProvider>
      <SearchResultProvider>
        <Layout
          currentLanguage={currentLanguage}
          lang={currentLanguage.toUpperCase()}
          customClass="relative"
          pageTemplate={"Search results page"}
        >
          <SEO
            title={seo?.title ?? title}
            uri={uri}
            lang={currentLanguage.toUpperCase()}
            headJson={seo?.headJSON ?? ""}
          />
          <div>
            <Section breakOut margins="my-0">
              <SearchResultControls />
            </Section>
            <PricesProvider>
              <SearchResultWidget results={results} />
            </PricesProvider>
          </div>
        </Layout>
        <SearchTool searchResultPage />
      </SearchResultProvider>
    </SearchProvider>
  )
}

export default i18nWrapper("SearchResult")(SearchResult)

export const query = graphql`
  query SINGLE_COMBINATION($areaId: String!, $typeId: String) {
    units: allWpPostTypeUnit(
      filter: {
        taxonomySiteAreas: { nodes: { elemMatch: { id: { eq: $areaId } } } }
        taxonomyUnitTypes: { nodes: { elemMatch: { id: { glob: $typeId } } } }
      }
    ) {
      nodes {
        ...UnitCardData
        wpParent {
          node {
            id
            ...SiteCardData
            ... on WpPostTypeSite {
              siteMeta {
                fieldEqSiteFieldsNumOfAvailableSpace
                fieldEqSiteFieldsNumOfAvailableUnits
              }
            }
          }
        }
      }
    }
    area: wpTaxonomySiteArea(id: { eq: $areaId }) {
      id
      slug
      name
    }
    type: wpTaxonomyUnitType(id: { eq: $typeId }) {
      id
      slug
      name
    }
    wp {
      themeSettings {
        themeSettings {
          groupGlobalPageLinks {
            mainSiteSearch {
              ... on WpPage {
                slug
              }
            }
          }
        }
      }
    }
  }
`
